import { cn, Image, Tooltip } from '@ds';
import { getColorsByOrganType, getOrganImg } from '@/utils/helpers';
import { DonorType, OrganType } from '@/api/apis/case';
import { OrganLastCallDays } from '@/api/apis/resources';
import { formatDays, getCsActualCertificates } from './utils';

type OrganLastCallCellProps = {
  isClinicalSpecialist?: boolean;
  donorType?: DonorType;
  shiftCertificates: OrganType[];
  employeeCertificates: OrganType[];
  lastCallDaysByOrgan: OrganLastCallDays;
  lastCallDaysByDonorAndOrgan?: Record<DonorType, OrganLastCallDays>;
  size?: 'sm' | 'md';
};

type LastCallTooltipContentProps = {
  minDays: { donor: DonorType; days: number | undefined }[];
  organ: OrganType;
  colors: string;
};

const LastCallTooltipContent = ({ minDays, organ, colors }: LastCallTooltipContentProps) => {
  return (
    <div className="flex flex-col gap-2">
      {minDays.map((data) => (
        <div key={`${data.donor}-${data.days}-${organ}`} className="flex gap-2 items-center">
          <div className={cn(colors, 'w-6 h-6 rounded-full flex justify-center items-center')}>
            <Image src={getOrganImg(organ)} alt={organ} />
          </div>
          <span className="text-m-m text-black">
            {data.donor}: {formatDays(data.days)}
          </span>
        </div>
      ))}
    </div>
  );
};

export const OrgansLastCall = ({
  isClinicalSpecialist = false,
  donorType,
  shiftCertificates,
  employeeCertificates,
  lastCallDaysByOrgan,
  lastCallDaysByDonorAndOrgan,
  size = 'md',
}: OrganLastCallCellProps) => {
  const organSpecializations = getCsActualCertificates(
    shiftCertificates,
    employeeCertificates,
    lastCallDaysByOrgan,
    lastCallDaysByDonorAndOrgan,
  );

  return (
    <div className={cn('flex flex-row items-center', { 'gap-2': size === 'sm', 'gap-3': size === 'md' })}>
      {organSpecializations?.map(({ days, byOrgan, isActive, organ }) => {
        const colors = getColorsByOrganType(organ);

        return (
          <Tooltip
            key={organ}
            isDisabled={!isClinicalSpecialist || !isActive}
            content={<LastCallTooltipContent minDays={byOrgan} organ={organ} colors={colors.bg50} />}
          >
            <div className={cn('flex items-center', { 'gap-1': size === 'sm', 'gap-2': size === 'md' })}>
              <Tooltip
                content="Certificate deactivated in admin panel"
                isDisabled={isActive || size === 'sm'}
                showArrow
              >
                <div
                  className={cn(
                    colors.bg50,
                    {
                      'grayscale opacity-50': !isActive,
                      'w-4 h-4': size === 'sm',
                      'w-6 h-6': size === 'md',
                    },
                    'rounded-full flex justify-center items-center',
                  )}
                >
                  <Image src={getOrganImg(organ)} alt={organ} />
                </div>
              </Tooltip>
              {!isClinicalSpecialist && donorType ? (
                <div className="text-center">
                  <div className="text-m-r">{formatDays(days)}</div>
                  <div className="text-s-r text-base-20">{donorType}</div>
                </div>
              ) : (
                <span className="text-m-r text-center">{formatDays(days)}</span>
              )}
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};
